// Portfolio — holdings & allocation page (route: "/portfolio"). Per-account: the
// `usePortfolioData` hook reads the account selected in the app-global auth slice
// and (re)fetches /portfolio when it changes. Data comes from the portfolio Redux
// slice (always the live backend); this page reads it through
// the hook and passes plain props down so section components stay pure.
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PageHead,
  Surface,
  Skeleton,
  // HIDDEN (Ask Risa, 2026-09-23): GenieTrigger,
  ErrorState,
  FundDetailModal,
} from '../../components/reusable';
import {
  PortfolioSummaryBar,
  HoldingsOverview,
  ActionStrip,
  HoldingsTabs,
  RecentActivity,
  ElectionStrip,
  ModelAllocationModal,
  usePortfolioData,
  useBenchmark,
} from '../../components/modules/portfolio';
// The avatar + profile dropdown, relocated out of the removed top navbar (FM-2406).
// HIDDEN (FM-2714): the participant avatar + profile dropdown. Kept for later — restore
// by uncommenting this import and the <AccountMenu /> element in the page head.
// import AccountMenu from '../../components/header/AccountMenu';
import { yesterday } from '../../utils/format';
import { fundSheetUrl, fundSheetViewUrl } from '../../config/constants';
import { useLegacyPortal } from '../../hooks';
import './portfolio.css';

// Page-level loading skeleton — mirrors the real section layout so nothing jumps
// on load. Same reusable Skeleton/Surface pattern as Dashboard & Manage Account.
function PortfolioSkeleton() {
  return (
    <>
      <Surface>
        <Skeleton variant="title" />
        <Skeleton variant="text" count={2} />
      </Surface>
      <Surface>
        <Skeleton variant="title" />
        <Skeleton variant="block" height={300} />
      </Surface>
      <Surface>
        <Skeleton variant="title" />
        <Skeleton variant="block" height={320} />
      </Surface>
      <Surface>
        <Skeleton variant="title" />
        <Skeleton variant="text" count={6} />
      </Surface>
    </>
  );
}

export default function Portfolio() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openLegacy, legacyHref } = useLegacyPortal();
  // The fund fact sheet is the PCS brand API's PDF (the same document the legacy portal
  // links to), re-served inline by our API so it renders in the modal and in a new tab
  // instead of downloading — this app does not generate PDFs itself.
  // `url` is what the modal's viewer rendered (a blob: URL when fetched directly).
  const openFundSheet = (ticker, url) => window.open(url || fundSheetViewUrl(ticker), '_blank', 'noopener,noreferrer');

  const { data, loading, error, refetch } = usePortfolioData();

  // Which benchmark this plan is measured against (FM-2101). The page no longer draws a
  // comparison of its own — the portfolio-vs-benchmark section is gone — but the benchmark
  // still answers one question the page asks: whether this plan's money is credited at a
  // guaranteed rate rather than invested, which changes what the page may offer and what it
  // may call the YTD figure. Always YTD, and it re-runs on a plan switch.
  const ytdBenchmark = useBenchmark(data, 'YTD');

  // The fund whose fact-sheet overlay is open (by ticker); null when closed.
  const [pickedTicker, setPickedTicker] = useState(null);
  const pickedFund = useMemo(
    () => (data.funds || []).find((f) => f.ticker === pickedTicker) || null,
    [data.funds, pickedTicker],
  );
  // The managed model whose allocation modal is open (by tree key); null when closed.
  // A fund picked inside it opens the fact sheet ON TOP — this stays open underneath.
  const [pickedModelKey, setPickedModelKey] = useState(null);
  const treeModels = useMemo(
    () => (data.holdingsTree || []).filter((it) => it.type === 'model'),
    [data.holdingsTree],
  );
  // Real as-of date from the backend (account.asOfDate) in live mode; mock mode
  // has no such field, so fall back to yesterday().
  const asOf = data.asOf || yesterday();

  // Per-plan gating (FM-2110). The plan record's permission flags decide what the
  // page offers; an action a plan doesn't grant is simply not rendered. The whole
  // record is handed down so each component asks the same question the design does.
  const plan = { actions: data.actions, planId: data.planId, status: data.planStatus };
  // A credited-rate plan reports no personal return, so it gets no ROR pill.
  const showRor = data.planActive !== false && data.actions?.canChangeElections === true;
  // A Penny Saved (cash balance): the employer directs the money at a guaranteed rate, so
  // there is no contribution or investment decision for the participant to make and the
  // page offers no Manage action. The permission flags can't say this on their own —
  // /portfolio ships no `actions` yet and the adapter has to default them to granted — so
  // the plan type answers instead, read off the benchmark selector's own classification
  // (`reason: 'planType'`, the same signal that relabels the YTD figure as credited).
  const employerDirected = ytdBenchmark?.reason === 'planType';

  if (error) {
    return (
      <div className="portfolio-page">
        <PageHead
          trail={[t('portfolio.trail')]}
          onNavigate={(crumb) => crumb?.to && navigate(crumb.to)}
          action={(
            <>
              {/* HIDDEN (Ask Risa, 2026-09-23): <GenieTrigger onClick={() => notify(t('portfolio.notify.risa'))} /> */}
              {/* HIDDEN (FM-2714): <AccountMenu /> */}
            </>
          )}
        />
        <ErrorState message={error} onRetry={refetch} />
      </div>
    );
  }

  return (
    <div className="portfolio-page" aria-busy={loading || undefined}>
      <PageHead
        trail={[t('portfolio.trail')]}
        onNavigate={(crumb) => crumb?.to && navigate(crumb.to)}
        action={(
          <>
            {/* HIDDEN (Ask Risa, 2026-09-23): <GenieTrigger onClick={() => notify(t('portfolio.notify.risa'))} /> */}
            {/* HIDDEN (FM-2714): <AccountMenu /> */}
          </>
        )}
      />

      {loading ? (
        <PortfolioSkeleton />
      ) : (
        <>
          <PortfolioSummaryBar summary={data.summary} balance={data.balance} asOf={asOf} />
          <HoldingsOverview
            funds={data.funds}
            items={data.holdingsTree}
            holdings={data.holdings}
            benchmark={ytdBenchmark}
            plan={plan}
            onPickFund={setPickedTicker}
            onPickModel={setPickedModelKey}
          />

          {/* One account action (FM-2110 Item 2) plus two read-only lookups — the
              transactional surfaces live behind Manage Account, not on this page. */}
          <ActionStrip
            actions={data.actions}
            employerDirected={employerDirected}
            showRor={showRor}
            portalHref={legacyHref}
            onPortalNavigate={openLegacy}
          />

            {/* Promoted above the comparison row (FM-2110 Item 3). It hides itself for
              plans that take no participant elections — a cash-balance or ended plan
              has no future contributions to show. */}
          <ElectionStrip
            funds={data.funds}
            items={data.holdingsTree}
            models={data.models}
            plan={plan}
            changedDate={data.elections?.changedDate}
            onChangeElections={() => openLegacy('investments')}
          />

          <HoldingsTabs
            funds={data.funds}
            tree={data.holdingsTree}
            summary={data.summary}
            holdings={data.holdings}
            assetClasses={data.assetClasses}
            sources={data.sources}
            classSpark={data.classSpark}
            asOf={asOf}
            plan={plan}
            onPickFund={setPickedTicker}
            onPickModel={setPickedModelKey}
          />

          <RecentActivity
            items={data.activity}
            onViewAll={() => openLegacy('history')}
          />
        </>
      )}

      {/* Before FundDetailModal on purpose: the sheet mounts later, so it stacks on top. */}
      <ModelAllocationModal
        open={!!pickedModelKey}
        models={treeModels}
        activeKey={pickedModelKey}
        onChangeModel={setPickedModelKey}
        plan={plan}
        onPickFund={setPickedTicker}
        onClose={() => setPickedModelKey(null)}
      />
      <FundDetailModal
        open={!!pickedTicker}
        fund={pickedFund}
        detail={data.fundDetail?.[pickedTicker]}
        asOf={asOf}
        sheetUrl={pickedTicker ? fundSheetViewUrl(pickedTicker) : undefined}
        sheetSourceUrl={pickedTicker ? fundSheetUrl(pickedTicker) : undefined}
        onClose={() => setPickedTicker(null)}
        onOpenExternal={(url) => openFundSheet(pickedTicker, url)}
        // The fallback when the sheet can't be shown inline: the brand API's own PDF link.
        onDownload={() => window.open(fundSheetUrl(pickedTicker), '_blank', 'noopener,noreferrer')}
      />
    </div>
  );
}
